import { SEO } from '@/components/SEO';

export default function RefundPolicy() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      <SEO 
        title="Refund Policy - QuickFreeInvoice" 
        description="Understand our refund policies for QuickFreeInvoice Pro subscriptions, including the 7-day money-back guarantee and cancellation terms." 
        keywords="invoice refund policy, payment refund, subscription cancellation"
      />
      <h1 className="font-heading text-4xl font-bold mb-8">Refund Policy</h1>
      
      <div className="prose prose-lg max-w-none space-y-8">
        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">1. Overview</h2>
          <p className="text-muted-foreground leading-relaxed">
            At quickfreeinvoicetool.com, we strive to provide excellent service to all our users. This Refund Policy 
            outlines the circumstances under which refunds may be issued for Pro subscriptions.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">2. Free Tier</h2>
          <p className="text-muted-foreground leading-relaxed">
            The Free tier of quickfreeinvoicetool.com does not require payment and therefore is not subject to refunds. 
            Users may use the Free tier indefinitely at no cost.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">3. Pro Subscription Refunds</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>General Policy:</strong> All Pro subscription payments are generally non-refundable. When you 
            purchase a Pro subscription, you gain immediate access to premium features, and the subscription is 
            charged monthly at $5 USD.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>7-Day Money-Back Guarantee:</strong> If you are not satisfied with your Pro subscription within 
            the first 7 days of your initial purchase, you may request a full refund. This guarantee applies only 
            to your first Pro subscription purchase and does not apply to subscription renewals.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong>Renewal Charges:</strong> Refunds are not provided for renewal charges. If you wish to avoid 
            future charges, please cancel your subscription before the next billing date. You will retain access 
            to Pro features until the end of your current billing period.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">4. How to Request a Refund</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            To request a refund within the 7-day money-back guarantee period, please contact us at:
          </p>
          <p className="text-muted-foreground leading-relaxed mb-3">
            <strong>Email:</strong>{' '}
            <a href="mailto:support@quickfreeinvoicetool.com" className="text-primary hover:underline">
              support@quickfreeinvoicetool.com
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Please include your account email address and a brief explanation of why you are requesting a refund. 
            We will process eligible refund requests within 5-7 business days.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">5. Refund Processing</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            Approved refunds will be processed through the original payment method used for the purchase (Paddle). 
            The timeframe for the refund to appear in your account depends on your payment provider:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>Credit/Debit Cards: 5-10 business days</li>
            <li>PayPal: 3-5 business days</li>
            <li>Other payment methods: Up to 14 business days</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">6. Subscription Cancellation</h2>
          <p className="text-muted-foreground leading-relaxed mb-3">
            You may cancel your Pro subscription at any time through the Subscription page in your account. Upon 
            cancellation:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
            <li>You will continue to have Pro access until the end of your current billing period</li>
            <li>No refund will be issued for the remaining days in the billing period</li>
            <li>Your subscription will not renew for the next billing cycle</li>
            <li>You will be automatically downgraded to the Free tier at the end of the billing period</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">7. Exceptional Circumstances</h2>
          <p className="text-muted-foreground leading-relaxed">
            In exceptional circumstances, such as service outages, technical issues preventing use of the Service, 
            or billing errors, we may issue refunds at our discretion. Please contact our support team to discuss 
            your specific situation.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">8. Disputes and Chargebacks</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have any concerns about a charge, please contact us directly before initiating a chargeback or 
            dispute with your payment provider. We are committed to resolving billing issues fairly and promptly. 
            Initiating a chargeback without contacting us first may result in immediate termination of your account.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">9. Account Termination</h2>
          <p className="text-muted-foreground leading-relaxed">
            If your account is terminated due to violation of our Terms and Conditions, no refund will be provided 
            for any unused portion of your subscription.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">10. Changes to This Policy</h2>
          <p className="text-muted-foreground leading-relaxed">
            We reserve the right to modify this Refund Policy at any time. Material changes will be communicated 
            via email to active Pro subscribers. The updated policy will be effective immediately upon posting.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold mb-4">11. Contact Information</h2>
          <p className="text-muted-foreground leading-relaxed">
            If you have any questions about this Refund Policy or need assistance with a refund request, please 
            contact us:
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            <strong>Email:</strong>{' '}
            <a href="mailto:support@quickfreeinvoicetool.com" className="text-primary hover:underline">
              support@quickfreeinvoicetool.com
            </a>
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Our support team typically responds within 24-48 hours during business days.
          </p>
        </section>

        <div className="border-t pt-8 mt-12">
          <p className="text-sm text-muted-foreground">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
}
